import { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const styles = {
  bg: { minHeight: '100vh', background: '#0d0d0d', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' },
  noise: { position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 20% 50%, #ff4d1c22 0%, transparent 50%), radial-gradient(circle at 80% 20%, #3d5af122 0%, transparent 40%)', pointerEvents: 'none' },
  card: { background: '#fff', borderRadius: 20, padding: '40px 36px', width: '100%', maxWidth: 400, position: 'relative', boxShadow: '0 30px 80px rgba(0,0,0,0.4)' },
  logo: { width: 52, height: 52, background: '#ff4d1c', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, marginBottom: 16 },
  title: { fontFamily: "'Bebas Neue'", fontSize: 32, letterSpacing: 2, color: '#111', marginBottom: 4 },
  sub: { fontSize: 13, color: '#888', marginBottom: 20 },
  tabRow: { display: 'flex', background: '#f4f4f4', borderRadius: 10, padding: 4, marginBottom: 20, gap: 4 },
  tab: { flex: 1, border: 'none', borderRadius: 8, padding: '9px 0', fontSize: 14, fontWeight: 500, color: '#888', background: 'transparent', cursor: 'pointer' },
  tabActive: { background: '#fff', color: '#111', fontWeight: 700, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  input: { width: '100%', border: '1.5px solid #e5e5e5', borderRadius: 9, padding: '11px 14px', fontSize: 15, color: '#111', background: '#fafafa', marginBottom: 12, transition: 'all 0.2s', boxSizing: 'border-box', display: 'block' },
  btn: { width: '100%', background: '#ff4d1c', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 0', fontSize: 15, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer', marginTop: 4 },
  err: { color: '#cc2200', fontSize: 13, marginBottom: 10, textAlign: 'center', padding: '8px 12px', background: '#fff0ee', borderRadius: 8 },
  succ: { color: '#007a50', fontSize: 13, marginBottom: 10, textAlign: 'center', padding: '8px 12px', background: '#e6fff5', borderRadius: 8 },
  switch: { textAlign: 'center', fontSize: 13, color: '#aaa', marginTop: 16 },
  link: { color: '#ff4d1c', cursor: 'pointer', fontWeight: 600 },
  hint: { fontSize: 11, color: '#bbb', textAlign: 'center', marginTop: 12 },
  forgot: { textAlign: 'right', fontSize: 12, color: '#ff4d1c', cursor: 'pointer', fontWeight: 600, marginTop: -6, marginBottom: 12 },
  back: { textAlign: 'center', fontSize: 13, color: '#aaa', marginTop: 12, cursor: 'pointer' },
};

export default function AuthScreen() {
  const { signIn, signUp, sendPasswordReset, updatePassword, needsPasswordReset } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (needsPasswordReset) setMode('reset');
  }, [needsPasswordReset]);

  const reset = (m) => { setMode(m); setErr(''); setSuccess(''); setEmail(''); setPassword(''); setConfirmPass(''); setDisplayName(''); };

  const handleLogin = async () => {
    if (!email || !password) return setErr('Please fill in all fields.');
    setLoading(true); setErr('');
    const { error } = await signIn(email, password);
    setLoading(false);
    if (error) setErr(error.message);
  };

  const handleSignup = async () => {
    if (!email || !password || !confirmPass) return setErr('Please fill in all fields.');
    if (password !== confirmPass) return setErr("Passwords don't match.");
    if (password.length < 6) return setErr('Password must be at least 6 characters.');
    setLoading(true); setErr('');
    const { error } = await signUp(email, password, displayName || email.split('@')[0]);
    setLoading(false);
    if (error) { setErr(error.message); }
    else { setSuccess('Account created! Check your email to confirm, then log in.'); }
  };

  const handleForgot = async () => {
    if (!email) return setErr('Please enter your email address.');
    setLoading(true); setErr('');
    const { error } = await sendPasswordReset(email);
    setLoading(false);
    if (error) setErr(error.message);
    else setSuccess('Reset link sent! Check your email.');
  };

  const handleUpdatePassword = async () => {
    if (!password || !confirmPass) return setErr('Please fill in both fields.');
    if (password !== confirmPass) return setErr("Passwords don't match.");
    if (password.length < 6) return setErr('Password must be at least 6 characters.');
    setLoading(true); setErr('');
    const { error } = await updatePassword(password);
    setLoading(false);
    if (error) setErr(error.message);
    else setSuccess('Password updated! You can now log in.');
  };

  const onKey = (e, fn) => e.key === 'Enter' && fn();

  return (
    <div style={styles.bg}>
      <div style={styles.noise} />
      <div style={styles.card}>
        <div style={styles.logo}>💪</div>
        <h1 style={styles.title}>My Fitness App</h1>
        <p style={styles.sub}>Your personal training companion</p>

        {mode !== 'forgot' && mode !== 'reset' && (
          <div style={styles.tabRow}>
            <button style={{ ...styles.tab, ...(mode === 'login' ? styles.tabActive : {}) }} onClick={() => reset('login')}>Log In</button>
            <button style={{ ...styles.tab, ...(mode === 'signup' ? styles.tabActive : {}) }} onClick={() => reset('signup')}>Sign Up</button>
          </div>
        )}

        {mode === 'forgot' && (
          <>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>Enter your email and we'll send you a reset link.</p>
            <input style={styles.input} placeholder="Email address" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => onKey(e, handleForgot)} />
          </>
        )}

        {mode === 'reset' && (
          <>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>Choose a new password for your account.</p>
            <input style={styles.input} placeholder="New password" type="password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => onKey(e, handleUpdatePassword)} />
            <input style={styles.input} placeholder="Confirm new password" type="password"
              value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)}
              onKeyDown={(e) => onKey(e, handleUpdatePassword)} />
          </>
        )}

        {mode === 'login' && (
          <>
            <input style={styles.input} placeholder="Email address" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => onKey(e, handleLogin)} />
            <input style={styles.input} placeholder="Password" type="password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => onKey(e, handleLogin)} />
            <p style={styles.forgot} onClick={() => reset('forgot')}>Forgot password?</p>
          </>
        )}

        {mode === 'signup' && (
          <>
            <input style={styles.input} placeholder="Display name (e.g. Arjun)"
              value={displayName} onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={(e) => onKey(e, handleSignup)} />
            <input style={styles.input} placeholder="Email address" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => onKey(e, handleSignup)} />
            <input style={styles.input} placeholder="Password" type="password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => onKey(e, handleSignup)} />
            <input style={styles.input} placeholder="Confirm password" type="password"
              value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)}
              onKeyDown={(e) => onKey(e, handleSignup)} />
          </>
        )}

        {err && <div style={styles.err}>{err}</div>}
        {success && <div style={styles.succ}>{success}</div>}

        {mode !== 'reset' || !success ? (
          <button style={{ ...styles.btn, opacity: loading ? 0.7 : 1 }}
            onClick={mode === 'login' ? handleLogin : mode === 'signup' ? handleSignup : mode === 'forgot' ? handleForgot : handleUpdatePassword}
            disabled={loading}>
            {loading ? 'Please wait…' : mode === 'login' ? 'Log In →' : mode === 'signup' ? 'Create Account →' : mode === 'forgot' ? 'Send Reset Link →' : 'Update Password →'}
          </button>
        ) : null}

        {(mode === 'login' || mode === 'signup') && (
          <p style={styles.switch}>
            {mode === 'login' ? 'New here? ' : 'Already have an account? '}
            <span style={styles.link} onClick={() => reset(mode === 'login' ? 'signup' : 'login')}>
              {mode === 'login' ? 'Sign up' : 'Log in'}
            </span>
          </p>
        )}
        {mode === 'forgot' && (
          <p style={styles.back} onClick={() => reset('login')}>← Back to log in</p>
        )}
        <p style={styles.hint}>Passwords are hashed by Supabase. We never store them in plain text.</p>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; font-family: 'DM Sans', sans-serif; }
        input:focus { outline: none; border-color: #ff4d1c !important; box-shadow: 0 0 0 3px rgba(255,77,28,0.15); }
        button { cursor: pointer; transition: all 0.15s ease; }
        button:hover { opacity: 0.88; }
        button:active { transform: scale(0.97); }
      `}</style>
    </div>
  );
}
